import { useMemo } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Button, Divider, Grid, MenuItem, Paper, TextField, Typography } from '@mui/material';
import { useBookingFlowContext } from '../../../booking-flow/BookingFlowProvider';
import { PhoneField } from '../../../components/PhoneField';
import type { TransferFlowState } from '../flowConfig';

const passengerSchema = z.object({
  firstName: z.string().min(1, 'Ad gerekli'),
  lastName: z.string().min(1, 'Soyad gerekli'),
  gender: z.string().min(1, 'Cinsiyet gerekli'),
  birthDate: z.string().optional(),
  paxType: z.string(),
  paxReferanceId: z.string().optional(),
});

const schema = z.object({
  contactName: z.string().min(1, 'Ad gerekli'),
  contactLastName: z.string().min(1, 'Soyad gerekli'),
  contactEmail: z.string().email('Geçerli bir e-posta girin'),
  contactPhone: z.string().min(1, 'Telefon gerekli'),
  passengers: z.array(passengerSchema).min(1, 'En az bir yolcu gerekli'),
});

type FormValues = z.infer<typeof schema>;

export function TransferPassengerStep() {
  const flow = useBookingFlowContext<TransferFlowState>();

  const breakdowns = useMemo(
    () => flow.state.cart?.transferBookings?.[0]?.passengerBreakdowns ?? [],
    [flow.state.cart]
  );

  const defaultPassengers = useMemo(
    () =>
      breakdowns.length > 0
        ? breakdowns.map((b) => ({
            firstName: '',
            lastName: '',
            gender: '',
            birthDate: '',
            paxType: b.paxReference?.paxCode ?? 'ADT',
            paxReferanceId: b.paxReference?.paxReferanceId ?? '',
          }))
        : [{ firstName: '', lastName: '', gender: '', birthDate: '', paxType: 'ADT', paxReferanceId: '' }],
    [breakdowns]
  );

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      contactName: '',
      contactLastName: '',
      contactEmail: '',
      contactPhone: '',
      passengers: defaultPassengers,
    },
  });

  const { fields } = useFieldArray({ control, name: 'passengers' });

  const onSubmit = (values: FormValues) => {
    flow.runStep({
      contact: {
        name: values.contactName,
        lastName: values.contactLastName,
        email: values.contactEmail,
        phone: values.contactPhone,
      },
      passengers: values.passengers.map((p) => ({
        firstName: p.firstName,
        lastName: p.lastName,
        gender: p.gender,
        birthDate: p.birthDate || undefined,
        paxType: p.paxType,
        paxReferanceId: p.paxReferanceId || undefined,
        email: values.contactEmail,
        phone: values.contactPhone,
      })),
    });
  };

  return (
    <Paper sx={{ p: 3 }} component="form" onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="subtitle1" gutterBottom>
        İletişim Bilgileri
      </Typography>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            label="Ad"
            size="small"
            fullWidth
            error={!!errors.contactName}
            helperText={errors.contactName?.message}
            {...register('contactName')}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            label="Soyad"
            size="small"
            fullWidth
            error={!!errors.contactLastName}
            helperText={errors.contactLastName?.message}
            {...register('contactLastName')}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <TextField
            label="E-posta"
            size="small"
            fullWidth
            error={!!errors.contactEmail}
            helperText={errors.contactEmail?.message}
            {...register('contactEmail')}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Controller
            control={control}
            name="contactPhone"
            render={({ field }) => (
              <PhoneField
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                label="Telefon"
                error={!!errors.contactPhone}
                helperText={errors.contactPhone?.message}
              />
            )}
          />
        </Grid>
      </Grid>

      <Divider sx={{ mb: 2 }} />
      <Typography variant="subtitle1" gutterBottom>
        Yolcu Bilgileri
      </Typography>
      {fields.map((field, index) => (
        <Box key={field.id} sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Yolcu {index + 1} ({field.paxType})
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Ad"
                size="small"
                fullWidth
                error={!!errors.passengers?.[index]?.firstName}
                helperText={errors.passengers?.[index]?.firstName?.message}
                {...register(`passengers.${index}.firstName` as const)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Soyad"
                size="small"
                fullWidth
                error={!!errors.passengers?.[index]?.lastName}
                helperText={errors.passengers?.[index]?.lastName?.message}
                {...register(`passengers.${index}.lastName` as const)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                select
                label="Cinsiyet"
                size="small"
                fullWidth
                defaultValue=""
                error={!!errors.passengers?.[index]?.gender}
                helperText={errors.passengers?.[index]?.gender?.message}
                {...register(`passengers.${index}.gender` as const)}
              >
                <MenuItem value="Male">Erkek</MenuItem>
                <MenuItem value="Female">Kadın</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                label="Doğum Tarihi"
                type="date"
                size="small"
                fullWidth
                slotProps={{ inputLabel: { shrink: true } }}
                {...register(`passengers.${index}.birthDate` as const)}
              />
            </Grid>
          </Grid>
        </Box>
      ))}

      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="submit" variant="contained" disabled={isSubmitting || flow.status === 'running'}>
          {flow.status === 'running' ? 'Kaydediliyor…' : 'Devam Et'}
        </Button>
      </Box>
    </Paper>
  );
}
